// Global Instructions Rule Applied!
//
// Agent Access — Phase 3: grant-lifecycle orchestration for the Connected
// Agents screen. Session-scoped settings data (small, user-scoped, refetched
// after every action) — no Redux slice, mirroring the email_settings surface.

import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  fetchMyAgentGrants,
  updateAgentGrant,
  revokeAgentGrant,
  AgentGrantScopePayload
} from '../services/agentAccessService.ts'
import type { AgentGrant } from '../../../state_manager/data_models/agent_access_model.ts'

export const useAgentGrants = () => {
  const [grants, setGrants] = useState<AgentGrant[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchMyAgentGrants(token)
      if (res.error) showErrorToast(res.error)
      setGrants(res.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const updateGrant = useCallback(
    async (grantId: string, payload: AgentGrantScopePayload): Promise<boolean> => {
      setSavingId(grantId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await updateAgentGrant(grantId, payload, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to update access')
          return false
        }
        toast.success('Agent access updated')
        await refresh()
        return true
      } finally {
        setSavingId(null)
      }
    },
    [refresh]
  )

  const revoke = useCallback(
    async (grantId: string): Promise<boolean> => {
      setRevokingId(grantId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await revokeAgentGrant(grantId, token)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to revoke grant')
          return false
        }
        toast.success('Agent access revoked')
        await refresh()
        return true
      } finally {
        setRevokingId(null)
      }
    },
    [refresh]
  )

  return { grants, loading, savingId, revokingId, refresh, updateGrant, revoke }
}
